import React, { Fragment } from 'react';
import { withTranslation } from 'react-i18next';
import './navbar.scss';
import {
  HTMLSelect, Navbar, NavbarHeading,
  Classes, NavbarDivider, Alignment,
  NavbarGroup, Button,
} from '@blueprintjs/core';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import UserMenu from 'components/UserMenu';
import { RootState } from 'data/root.reducers';
import { isMultitenantEnabled } from 'data/supportedFeatures/supportedFeatures.selectors';
import * as authenticationActions from 'data/authentication/authentication.actions';
import ss8logo from '../../assets/ss8-logo.png';
import { getPasswordAndGoToPage } from '../../policies/policy-actions';
import { getAllDFs, setDF } from '../../global/global-actions';
import { getDXSettings } from '../../xcipio/discovery-xcipio-actions';
import { fetchLocationList } from '../../data/location/location.actions';
import { createSelect } from '../form/form-renderer';
import navbarConfig from './navbar-config';
import AuthUtils from '../../authentication/auth-utils';
import SubNavComponent from './sub-nav-component';
import { AUTH, getAuth } from '../../utils';
import { getRouteRootPathname, getNavItemLandingRoute } from './utils';
import { NavbarItem } from './types';

export class Nav extends React.Component<any, any> {
  private ref: any;

  private availableNavBarConfig: NavbarItem[] = [];

  constructor(props: any) {
    super(props);
    this.ref = React.createRef();
    this.state = {
      active: null,
      curPathBeforePasswordUpdate: '',
      isOpen: true,
      skipSubNavRouting: false,
      logiScriptLoaded: false,
    };
  }

  handleNavItemClick = (e: any) => {
    // Set the state to active on the selected main nav item.
    // Get the respective navbarConfig for the clicked target.
    const currentId = e.currentTarget.id;
    for (let index = 0; index < this.availableNavBarConfig.length; index += 1) {
      if (this.availableNavBarConfig[index].id === e.currentTarget.id) {
        this.ref.current.state.active = this.state.active.children ?
          { ...this.state.active.children[0] } :
          null;
        this.setState({ active: this.availableNavBarConfig[index], skipSubNavRouting: false }, () => {
          if (currentId.startsWith('external-')) {
            this.props.history.push(`${this.availableNavBarConfig[index].route}#t=${Date.now()}`);
          } else {
            this.props.history.push(getNavItemLandingRoute(
              this.availableNavBarConfig[index], this.props.authentication.privileges,
            ));
          }
        });
        break;
      }
    }
  }

  getSelectedNavItemFromRouter() {
    const { location: { pathname } } = this.props;

    const navBarMap: {[key: string]: any} = {};
    this.availableNavBarConfig.forEach((config) => {
      if (config.children && config.children.length > 1) {
        config.children.forEach((childConfig: any) => {
          navBarMap[childConfig.route] = config;
        });
      } else {
        navBarMap[config.route] = config;
      }
    });

    return navBarMap[pathname] || navBarMap[getRouteRootPathname(pathname)] || null;
  }

  getDefaultSelectedNavItem = () => {
    for (let index = 0; index < this.availableNavBarConfig.length; index += 1) {
      if (this.availableNavBarConfig[index].defaultSelected) {
        return this.availableNavBarConfig[index];
      }
    }
    return this.availableNavBarConfig.length > 0 ? this.availableNavBarConfig[0] : null;
  }

  isActive = (selection: any) => {
    if (this.state.active != null && this.state.active.id === selection) return true;
    return false;
  }

  handlePasswordUpdate = () => {
    this.props.setPasswordUpdateFlag();
    this.setState({ curPathBeforePasswordUpdate: this.props.history.location.pathname });
    const body = {
      pathname: '/authentication/passwordupdate',
      username: this.props.authentication.userId,
      curpath: this.props.history.location.pathname,
    };
    this.props.getPasswordAndGoToPage(this.props.history, body);
  }

  loadLogiScript = () => {
    if (!this.state.logiScriptLoaded && this.props.externalDashboardIP !== '') {
      let script: HTMLScriptElement | null = null;
      script = document.createElement('script');
      script.src = new URL(`${this.props.externalDashboardIP}/composer/embed/embed.js`).href;
      script.async = true;
      script.setAttribute('data-name', 'composer-embed-manager');
      document.body.appendChild(script);
      this.setState({ logiScriptLoaded: true });
    }
  }

  componentDidMount() {
    if (this.props.authentication) {
      const { isAuthenticated, userId: user } = this.props.authentication;

      if (isAuthenticated && getAuth(AUTH.TOKEN)) {
        this.props.doCheckToken(getAuth(AUTH.TOKEN));

        const {
          externalDashboardIP,
          logiOAuthClientId,
          logiOAuthClientName,
          logiSupervisorUserName,
          logiSupervisorPswd,
        } = this.props;

        this.props.checkLogiToken({
          user,
          externalDashboardIP,
          logiOAuthClientId,
          logiOAuthClientName,
          logiSupervisorUserName,
          logiSupervisorPswd,
        });
      }
    }
  }

  componentDidUpdate(prevProps: any) {
    const { isAuthenticated, privileges } = this.props.authentication;

    if (getRouteRootPathname(prevProps.location.pathname) !== getRouteRootPathname(this.props.location.pathname)) {
      const selectedNavItem = this.getSelectedNavItemFromRouter();
      if (selectedNavItem != null) {
        return this.setState({ active: selectedNavItem, skipSubNavRouting: true });
      }
    }
    this.loadLogiScript();
    if ((isAuthenticated && !prevProps.authentication.isAuthenticated) ||
      (privileges !== prevProps.authentication.privileges) ||
      (this.props.isXmsMenusEnabled !== prevProps.isXmsMenusEnabled) ||
      (this.props.isSecurityGatewayEnabled !== prevProps.isSecurityGatewayEnabled) ||
      (this.props.isExternalDashboardEnabled !== prevProps.isExternalDashboardEnabled) ||
      (this.props.isNetworkTopologyEnabled !== prevProps.isNetworkTopologyEnabled) ||
      (this.props.isAlarmFeatureEnabled !== prevProps.isAlarmFeatureEnabled) ||
      (this.props.isEWarrantEnabled !== prevProps.isEWarrantEnabled) ||
      (this.props.isMultitenantEnabled !== prevProps.isMultitenantEnabled) ||
      (this.props.isAlarmNotificationProfileEnabled !== prevProps.isAlarmNotificationProfileEnabled)) {
      // Reloading in case of browser refresh
      this.props.getDXSettings();
      this.props.fetchLocationList();
      // Make a call to dfs only if you have access.
      if (AuthUtils.hasPermissions(['df_view'], privileges)) {
        this.props.getAllDFs();
      }

      this.availableNavBarConfig = navbarConfig(this.props.isExternalDashboardEnabled,
        this.props.isSecurityGatewayEnabled, this.props.isXmsMenusEnabled, this.props.isNetworkTopologyEnabled,
        this.props.isAlarmFeatureEnabled, this.props.isAlarmNotificationProfileEnabled,
        this.props.externalDashboardId, this.props.systemHealthDashboardId,
        this.props.aggLicensesDashboardId, this.props.xcipioLicensesDashboardId, this.props.openSearchUrl,
        this.props.authentication.userId, this.props.isEWarrantEnabled, this.props.isMultitenantEnabled)
        .filter((navItem: any) => {
          return AuthUtils.hasPermissions(navItem.permissions, privileges);
        });

      const selectedNavItem = this.getSelectedNavItemFromRouter();
      if (selectedNavItem != null) {
        return this.setState({ active: selectedNavItem, skipSubNavRouting: false });
      }

      const defaultNavItem = this.getDefaultSelectedNavItem();
      if (defaultNavItem != null) {
        this.setState({ active: defaultNavItem, skipSubNavRouting: false }, () => {
          this.props.history.push(getNavItemLandingRoute(defaultNavItem, privileges));
        });
      }
    }
  }

  render() {
    const {
      location,
      authentication: {
        userId,
        firstName,
        lastName,
      },
      handleLogout,
      t,
    } = this.props;

    const dfValues = createSelect(this.props.global.dfs, 'id', 'name');
    return (
      <Fragment>
        <Navbar fixedToTop className="bp3-dark">
          <NavbarGroup align={Alignment.LEFT}>
            <NavbarHeading>
              <img height={26} style={{ marginTop: '5px' }} src={ss8logo} alt={t('common.menu.ss8Logo')} />
              <span>{t('common.menu.xcipio')}</span>
            </NavbarHeading>
            <NavbarDivider />
            {this.availableNavBarConfig.map((navItem: any, i: number) => {
              const {
                text,
                icon,
                id,
                permissions,
              } = navItem;
              if (!AuthUtils.hasPermissions(permissions,
                this.props.authentication.privileges)) return null;
              return (
                <Button
                  key={i}
                  className={Classes.MINIMAL}
                  icon={icon}
                  active={this.isActive(id)}
                  id={id}
                  text={text}
                  onClick={this.handleNavItemClick}
                />
              );
            })}
          </NavbarGroup>
          <NavbarGroup align={Alignment.RIGHT}>
            {this.props.global.dfs.length > 1 && (
            <HTMLSelect
              name="setDF"
              options={dfValues}
              onChange={(e: any) => this.props.setDF(parseInt(e.target.value, 10))}
              value={this.props.global.selectedDF}
            />
            )}
            <UserMenu
              username={userId}
              firstName={firstName}
              lastName={lastName}
              onLogout={handleLogout}
              passwordUpdate={this.handlePasswordUpdate}
            />
          </NavbarGroup>
        </Navbar>
        <SubNavComponent
          location={location}
          subNavItems={
          this.state.active !== null ?
            this.state.active.children ? this.state.active.children : [] : null
          }
          skipSubNavRouting={this.state.skipSubNavRouting}
          ref={this.ref}
          {...this.props}
        />
      </Fragment>
    );
  }
}

const mapStateToProps = ({
  authentication,
  global,
  policies,
  discoveryXcipio: {
    discoveryXcipioSettings: {
      isExternalDashboardEnabled,
      isSecurityGatewayEnabled,
      isXmsMenusEnabled,
      isNetworkTopologyEnabled,
      isAlarmFeatureEnabled,
      isAlarmNotificationProfileEnabled,
      xcEwarrantEnable,
      externalDashboardIP,
      logiOAuthClientId,
      logiOAuthClientName,
      logiSupervisorUserName,
      logiSupervisorPswd,
      externalDashboardId,
      systemHealthDashboardId,
      aggLicensesDashboardId,
      xcipioLicensesDashboardId,
      openSearchUrl
    },
  },
}: RootState, { location }: any) => ({
  authentication,
  global,
  policies,
  isExternalDashboardEnabled,
  isSecurityGatewayEnabled,
  isXmsMenusEnabled,
  isNetworkTopologyEnabled,
  isAlarmFeatureEnabled,
  isAlarmNotificationProfileEnabled,
  isEWarrantEnabled: xcEwarrantEnable ?? false,
  externalDashboardIP,
  logiOAuthClientId,
  logiOAuthClientName,
  logiSupervisorUserName,
  logiSupervisorPswd,
  location,
  externalDashboardId,
  systemHealthDashboardId,
  aggLicensesDashboardId,
  xcipioLicensesDashboardId,
  openSearchUrl
});

const mapStateToPropsWithFeatures = (state: RootState, ownProps: any) => ({
  ...mapStateToProps(state, ownProps),
  isMultitenantEnabled: isMultitenantEnabled(state),
});

const mapDispatchToProps = (dispatch: any) => bindActionCreators({
  checkLogiToken: authenticationActions.checkLogiToken,
  handleLogout: authenticationActions.handleLogout,
  getAllDFs,
  setDF,
  doCheckToken: authenticationActions.doCheckToken,
  setPasswordUpdateFlag: authenticationActions.setPasswordUpdateFlag,
  getPasswordAndGoToPage,
  getDXSettings,
  fetchLocationList,
}, dispatch);

export default withTranslation()(
  withRouter(connect(mapStateToPropsWithFeatures, mapDispatchToProps, null, { forwardRef: true })(Nav)),
);
